import {
  cloneElement,
  CSSProperties,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AnimationIcon from '@mui/icons-material/Animation';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import BarChartIcon from '@mui/icons-material/BarChart';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import PaletteIcon from '@mui/icons-material/Palette';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import TerminalIcon from '@mui/icons-material/Terminal';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import { TABLE_THEME_ORDER, TABLE_THEMES, TableThemeId } from 'constants/tableThemes';
import GlobalContext from 'contexts/GlobalContext';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import TableSettingsContext from 'contexts/TableSettingsContext';
import {
  FEATURE_FLAG_PATHS,
  FEATURE_FLAGS,
  useFeatureFlag,
  useFeatureFlagExposure
} from 'featureFlags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useOrientation } from 'hooks/useOrientation';
import { usePanelSlide } from 'hooks/usePanelSlide';
import { Promotion } from 'reducers/playerPromos';
import { patchPlayerProfile } from 'reducers/user';
import { trackPendoEvent } from 'services/PendoService';
import {
  BiometricBlocked,
  disableBiometric as disableNativeBiometric,
  enrollBiometric as enrollNativeBiometric,
  getBiometricBlockedReason,
  getBiometryLabel,
  isBiometricAvailable,
  isBiometricEnrolled,
  openIOSSettings
} from 'services/nativeBiometric';
import { clearAuth, clearPostLoginState, decodeJwtPayload, getAccessToken, getAuthToken } from 'utils/auth';
import { clearBiometricCredentials } from 'utils/clearBiometricCredentials';
import { currencyFormatter } from 'utils/functions';
import { safeAreaPanelInsets } from 'utils/safeArea';
import {
  APP_DOMAIN,
  BACKOFFICE_API_BASE,
  HELP_CENTER_URL,
  PRICING_URL,
  PROMOS_URL,
  REFERRALS_URL,
  TOURNAMENT_SCHEDULE_URL
} from 'utils/urls';

import {
  BiometryKind,
  biometryTitle,
  SensorGlyph
} from 'components/BiometricPrototype/biometricGlyphs';
import {
  BiometricSurfaceHost,
  BiometricUnavailableCard
} from 'components/BiometricPrototype/BiometricSurfaces';
import { CashierContext } from 'components/Cashier/CashierContext';
import { isDebugConsoleEnabled, setDebugConsoleEnabled } from 'components/DebugConsole';
import { DEBUG_CONSOLE_TOGGLE_EVENT } from 'components/Debug/debugConsolePreference';
import {
  PERF_HUD_TOGGLE_EVENT,
  isPlayerPerfHudEnabled,
  setPlayerPerfHudEnabled
} from 'components/Debug/perfHudPreference';
import ForcePortrait from 'components/ForcePortrait/ForcePortrait';
import NotificationList from 'components/Notifications/NotificationList';
import { useNotifications } from 'components/Notifications/useNotifications';
import { AccountPromoBanner, PromoDetailsModal } from 'components/PlayerPromos/PlayerPromos';
import { useLobbyCardSize } from 'components/SaloonLobby/lobbyCardSize';
import TableViewSizeControl from 'components/TableSettings/TableViewSizeControl';
import TableViewSizePreview from 'components/TableSettings/TableViewSizePreview';
import VersionLabel from 'components/VersionLabel';
import { useStackTablesToggle } from 'components/TableHeader/useStackTablesToggle';

import ProfilePanel from './ProfilePanel';

// Outer menu panel — see EN-9838.
type SubView = 'profile' | 'notifications' | 'settings' | 'tableTheme' | null;
// Off-screen position the panel slides in from (right-hand edge).
const PANEL_HIDDEN = { x: '100%', y: '0%' };

const openExternalUrl = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

// Hover/press helper for inline-styled controls (no :hover/:active with
// CSSProperties). Applies `hover` styles on pointer enter AND on touch start
// (touch devices don't fire hover — without the touch handlers there'd be no
// tap feedback on mobile), restores `rest` on leave / touch end.
const hoverStyle = (hover: Partial<CSSStyleDeclaration>, rest: Partial<CSSStyleDeclaration>) => ({
  onMouseEnter: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, hover),
  onMouseLeave: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, rest),
  onTouchStart: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, hover),
  onTouchEnd: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, rest),
  onTouchCancel: (e: { currentTarget: HTMLElement }) => Object.assign(e.currentTarget.style, rest)
});

// Shared hover for the small round close/back icon buttons.
const iconBtnHover = hoverStyle(
  { background: 'rgba(255,255,255,0.12)', color: '#fff' },
  { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)' }
);

const AccountMenuPanel = ({
  open,
  onClose,
  initialView = null
}: {
  open: boolean;
  onClose: () => void;
  initialView?: SubView;
}) => {
  const dispatch = useAppDispatch();
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const playerAvatar = useAppSelector((state) => state.user.playerAvatar);
  const displayName = useAppSelector(
    (state) => state.user.displayName || state.user.playerName || 'Player'
  );
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const cashBalance = useAppSelector((state) => state.user.playerBankAmount);
  const promoBalance = useAppSelector((state) => state.user.playerChargeBalance);
  const activePromo = useAppSelector((state) =>
    (state.playerPromos?.promotions || []).find((p: any) => p?.is_active)
  );
  // Cashier + Account Profile are blocked during maintenance (Unity parity):
  // no deposits/withdrawals or profile edits while the platform is down.
  const isMaintenance = useAppSelector(
    (state) => (state as any).maintenanceSchedule?.isMaintenance
  );
  const { openCashier, setOpenedFromMenu } = useContext(CashierContext);
  const { sortedNotifications, unreadCount, markNotificationRead } = useNotifications();
  const { isMuted, setIsMuted } = useContext(GlobalContext);
  const tableSettings = useContext(TableSettingsContext);
  const animations = useAnimationPreferences();
  // Shared with TableMenuPanel — one definition for both menus.
  const stackTablesToggle = useStackTablesToggle();
  // Lobby tile density — shared store so changing it here resizes the live
  // lobby tiles immediately (moved out of the Table Filters drawer).
  const { cardSize, setCardSize } = useLobbyCardSize();
  const [debugConsoleOn, setDebugConsoleOn] = useState(() =>
    isDebugConsoleEnabled(playerGUID)
  );
  useEffect(() => {
    const syncDebugConsolePreference = () =>
      setDebugConsoleOn(isDebugConsoleEnabled(playerGUID));
    syncDebugConsolePreference();
    window.addEventListener(DEBUG_CONSOLE_TOGGLE_EVENT, syncDebugConsolePreference);
    return () =>
      window.removeEventListener(DEBUG_CONSOLE_TOGGLE_EVENT, syncDebugConsolePreference);
  }, [playerGUID]);
  // See ProfilePanel: native reads the keystore, web reads the passkey cookie.
  const isNativeApp = Capacitor.isNativePlatform();
  const [passkeyEnrolled, setPasskeyEnrolled] = useState(() =>
    isNativeApp ? isBiometricEnrolled() : document.cookie.includes('hj_passkey=enrolled')
  );
  // Re-read on every open: enrollment can happen while this panel is mounted but
  // closed (the post-login BiometricEnrollmentPrompt enrolls from App level), and
  // a useState initializer only runs once — without this the toggle shows OFF
  // for a player who just enrolled seconds ago.
  useEffect(() => {
    if (!open) return;
    setPasskeyEnrolled(
      isNativeApp ? isBiometricEnrolled() : document.cookie.includes('hj_passkey=enrolled')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // AC10 — hide the setting unless real biometric hardware is enrolled.
  // WHY the sensor is unusable, so the row can say it (AC11/AC12). null = fine.
  const [biometricBlocked, setBiometricBlocked] = useState<BiometricBlocked | null>(null);
  const [showBiometricHelp, setShowBiometricHelp] = useState(false);
  // Sensor kind for the explainer's glyph and copy. On a device with nothing
  // enrolled iOS still reports the HARDWARE type, so this stays accurate even
  // when the sensor is unusable — which is exactly the case the card explains.
  const [biometricLabelKind, setBiometricLabelKind] = useState<BiometryKind>('faceid');
  // Never over-claim before the real capability check below resolves — same
  // philosophy as the native branch's own initial guess.
  const [biometricAvailableHere, setBiometricAvailableHere] = useState(false);
  useEffect(() => {
    if (!isNativeApp) {
      // Web has its own capability question: a platform authenticator
      // actually capable of user verification, not just the WebAuthn API
      // existing. `PublicKeyCredential !== undefined` is true on browsers/
      // OSes with no biometric/PIN hardware at all (e.g. only roaming
      // security-key support) — isUserVerifyingPlatformAuthenticatorAvailable()
      // is the real question, and matches what createBiometricCredential's
      // own `userVerification: 'required'` ceremony actually needs to succeed.
      let cancelled = false;
      if (typeof window.PublicKeyCredential === 'undefined') {
        setBiometricBlocked('unsupported');
        setBiometricAvailableHere(false);
        return;
      }
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then((available) => {
          if (cancelled) return;
          // The row still shows either way (AC11a) — this only decides
          // whether it says so instead of pretending the toggle would work.
          setBiometricBlocked(available ? null : 'unsupported');
          setBiometricAvailableHere(available);
        })
        .catch(() => {
          if (!cancelled) {
            setBiometricBlocked('unsupported');
            setBiometricAvailableHere(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    void getBiometricBlockedReason().then((reason) => {
      if (!cancelled) setBiometricBlocked(reason);
    });
    void getBiometryLabel().then((label) => {
      if (cancelled) return;
      const l = (label || '').toLowerCase();
      setBiometricLabelKind(
        l.includes('face') ? 'faceid' : l.includes('touch') ? 'touchid' : 'fingerprint'
      );
    });
    isBiometricAvailable().then((ok) => {
      if (!cancelled) setBiometricAvailableHere(ok);
    });
    return () => {
      cancelled = true;
    };
    // Re-checked on every open, not just mount: the blocked reason changes
    // OUTSIDE the app — the player follows "Open iOS Settings", enrolls a
    // face, and comes back. A mount-only read would keep telling them it
    // isn't set up (owner 2026-08-20).
  }, [isNativeApp, open]);
  const [isEnrollingBiometric, setIsEnrollingBiometric] = useState(false);
  const [biometricMsg, setBiometricMsg] = useState('');
  const playerEmail = useAppSelector((state) => state.user.playerEmail);
  const enableSecureLogin = useAppSelector(
    (state) => (state.user as any).enableSecureLogin as boolean | null
  );
  const profileLoaded = useAppSelector((state) => state.user.profileLoaded);
  // Biometric login is open to every signed-in player, however they signed
  // in (password, Google/Apple, OTP, passkey): the stored credential is this
  // session's refresh token, which every login method produces. The only
  // requirement is a session to store — a guest has nothing to enroll.
  // Guarded: getAccessToken() THROWS (rather than returning '') when the
  // token cookie is absent or not valid JSON. Unguarded, that throw takes
  // down the whole host header.
  const hasSession = (() => {
    try {
      return !!getAccessToken();
    } catch {
      return false;
    }
  })();
  // The credential is keyed by email, so an account without one (phone-only
  // OTP) could never complete enrollment — handleBiometricToggle bails, and
  // the prompt would come back on every open. Same gate as the auth app's
  // offer (LoginApp.js).
  const canEnrollBiometric = hasSession && !!playerEmail;
  const performanceHudAvailable = useFeatureFlag('performanceHud');
  const systemLogAvailable = useFeatureFlag('systemLog');
  const referralsEnabled = useFeatureFlag('referrals');
  const [performanceHudOn, setPerformanceHudOn] = useState(() =>
    isPlayerPerfHudEnabled(playerGUID)
  );
  useEffect(() => {
    const syncPerformanceHudPreference = () =>
      setPerformanceHudOn(isPlayerPerfHudEnabled(playerGUID));
    syncPerformanceHudPreference();
    window.addEventListener(PERF_HUD_TOGGLE_EVENT, syncPerformanceHudPreference);
    return () =>
      window.removeEventListener(PERF_HUD_TOGGLE_EVENT, syncPerformanceHudPreference);
  }, [playerGUID]);
  // Draft selection for the table-theme picker — committed to TableSettings only
  // when the player taps Save (mirrors the old ProfilePanel Table Settings flow).
  const [draftTheme, setDraftTheme] = useState<TableThemeId>(tableSettings.theme);

  const [subView, setSubView] = useState<SubView>(null);
  useFeatureFlagExposure('performanceHud', 'settings', open && subView === 'settings');
  useFeatureFlagExposure('systemLog', 'settings', open && subView === 'settings');
  useFeatureFlagExposure('reduceAnimations', 'settings', open && subView === 'settings');

  // The game only links to the standalone app. Its API owns access checks
  // and referral data, so an API outage must not remove this navigation entry.
  const referralProgramEnabled = referralsEnabled && hasSession;
  useFeatureFlagExposure('referrals', 'account_menu', open && subView === null);
  // Whether the Notifications view was entered straight from the lobby envelope
  // (vs. from the account-menu row). Drives where its Back button goes: the
  // lobby (close the panel) for a direct entry, the menu root otherwise.
  const [notifFromLobby, setNotifFromLobby] = useState(false);
  // Key of the menu row under the pointer — drives the row highlight plus the
  // icon/chevron turning orange on hover (see EN-9838 follow-up).
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [selectedPromo, setSelectedPromo] = useState<Promotion | null>(null);
  // Defer unmount until the close slide-out animation finishes — without this
  // the panel pops away instead of sliding off-screen.
  const [rendered, setRendered] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setRendered(true);
  }, [open]);

  // On open, jump to the requested view (e.g. the header envelope opens
  // 'notifications'); defaults to the root menu. Re-runs if the requested view
  // changes while already open, so clicking the envelope navigates there too.
  useEffect(() => {
    if (!open) {
      setSubView(null);
      return;
    }
    setSubView(initialView ?? null);
    // Opening straight to notifications means we came from the lobby envelope.
    setNotifFromLobby(initialView === 'notifications');
  }, [open, initialView]);

  // Deep-link: when 'open-profile-panel' fires (with optional field focus),
  // jump straight into the Account Profile sub-view so existing flows that
  // dispatched this event continue to land on the profile editor. Listener
  // is unconditional because the dispatcher fires the event before this
  // component renders the first time.
  useEffect(() => {
    const handleDeepLink = (event: Event) => {
      const detail = (event as CustomEvent<{ field?: string }>).detail;
      if (detail?.field) setSubView('profile');
    };
    window.addEventListener('open-profile-panel', handleDeepLink);
    return () => window.removeEventListener('open-profile-panel', handleDeepLink);
  }, []);

  // Slide animation — always horizontal (slide from right), on both desktop
  // and mobile. Defers unmount via `rendered` so the close tween plays out
  // instead of popping away.
  usePanelSlide({
    open,
    rendered,
    panelRef,
    backdropRef,
    hidden: PANEL_HIDDEN,
    onClosed: () => setRendered(false)
  });

  if (!rendered) return null;

  const handleOpenCashier = () => {
    setOpenedFromMenu(true);
    onClose();
    openCashier();
  };

  const handleLogout = async () => {
    // Local logout only — deliberately does NOT call clearBiometricCredentials.
    // A plain Logout tap should end this session, not un-enroll the device:
    // wiping the biometric credential here meant the very next login always
    // fell back to the password/social form instead of Face ID/fingerprint,
    // even though nothing about biometrics was ever explicitly turned off.
    // clearBiometricCredentials is still the right call for an actual
    // disable — see handleBiometricToggle(false) above, and
    // usePlayerProfile's reconciliation for a remote disable — both of
    // those are genuine "turn biometrics off" events; this button isn't.
    clearAuth();
    clearPostLoginState();
    window.location.reload();
  };

  const menuItems: {
    key: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    badge?: number;
    disabled?: boolean;
  }[] = [
    {
      key: 'cashier',
      label: 'Cashier',
      icon: <MonetizationOnOutlinedIcon style={menuIconStyle} />,
      onClick: handleOpenCashier,
      disabled: isMaintenance
    },
    {
      key: 'profile',
      label: 'Account Profile',
      icon: (
        <svg
          viewBox='0 0 24 24'
          width={18}
          height={18}
          fill='none'
          stroke='currentColor'
          strokeWidth={1.7}
          strokeLinecap='round'
          strokeLinejoin='round'>
          <path d='M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2' />
          <circle cx='12' cy='7' r='4' />
        </svg>
      ),
      onClick: () => setSubView('profile'),
      disabled: isMaintenance
    },
    {
      key: 'notifications',
      label: 'Notifications',
      icon: (
        <svg
          viewBox='0 0 24 24'
          width={18}
          height={18}
          fill='none'
          stroke='currentColor'
          strokeWidth={1.7}
          strokeLinecap='round'
          strokeLinejoin='round'>
          <path d='M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9' />
          <path d='M10.3 21a1.94 1.94 0 0 0 3.4 0' />
        </svg>
      ),
      onClick: () => {
        setNotifFromLobby(false);
        setSubView('notifications');
      },
      badge: unreadCount
    },
    {
      key: 'pricing',
      label: 'Pricing',
      icon: (
        <svg
          viewBox='0 0 24 24'
          width={18}
          height={18}
          fill='none'
          stroke='currentColor'
          strokeWidth={1.7}
          strokeLinecap='round'
          strokeLinejoin='round'>
          <circle cx='12' cy='12' r='9' />
          <circle cx='12' cy='12' r='2.5' />
          <path d='M12 1v3' />
          <path d='M12 20v3' />
          <path d='M1 12h3' />
          <path d='M20 12h3' />
        </svg>
      ),
      onClick: () => openExternalUrl(PRICING_URL)
    },
    {
      key: 'tournaments',
      label: 'Tournament Schedule',
      icon: (
        <svg
          viewBox='0 0 24 24'
          width={18}
          height={18}
          fill='none'
          stroke='currentColor'
          strokeWidth={1.7}
          strokeLinecap='round'
          strokeLinejoin='round'>
          <rect x='3' y='4' width='18' height='18' rx='2' ry='2' />
          <path d='M16 2v4' />
          <path d='M8 2v4' />
          <path d='M3 10h18' />
        </svg>
      ),
      onClick: () => openExternalUrl(TOURNAMENT_SCHEDULE_URL)
    },
    // Always present, whether or not a promo is active: the in-menu promo
    // banner only appears for an active promo, so it is not a way in (EN-10801).
    {
      key: 'promos',
      label: 'Promotions',
      icon: (
        <svg
          viewBox='0 0 24 24'
          width={18}
          height={18}
          fill='none'
          stroke='currentColor'
          strokeWidth={1.7}
          strokeLinecap='round'
          strokeLinejoin='round'>
          <path d='M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z' />
          <path d='M13 5v2' />
          <path d='M13 11v2' />
          <path d='M13 17v2' />
        </svg>
      ),
      onClick: () => openExternalUrl(PROMOS_URL)
    },
    ...(referralProgramEnabled ? [{
      key: 'referrals',
      label: 'Referrals',
      icon: (
        <svg
          width='20'
          height='20'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.7'
          strokeLinecap='round'
          strokeLinejoin='round'>
          <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
          <circle cx='9' cy='7' r='4' />
          <path d='M19 8v6' />
          <path d='M22 11h-6' />
        </svg>
      ),
      onClick: () => openExternalUrl(REFERRALS_URL)
    }] : []),
    {
      key: 'knowledge',
      label: 'Knowledge Base',
      icon: (
        <svg
          viewBox='0 0 24 24'
          width={18}
          height={18}
          fill='none'
          stroke='currentColor'
          strokeWidth={1.7}
          strokeLinecap='round'
          strokeLinejoin='round'>
          <circle cx='12' cy='12' r='10' />
          <path d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' />
          <path d='M12 17h.01' />
        </svg>
      ),
      onClick: () => openExternalUrl(HELP_CENTER_URL)
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: <SettingsIcon style={menuIconStyle} />,
      onClick: () => setSubView('settings')
    }
  ];

  const menuView = (
    <>
      <div style={headerStyle}>
        <div style={avatarSection}>
          <div style={avatarWrap}>
            {playerAvatar ? (
              <img
                src={`https://media.${APP_DOMAIN}/images/${playerAvatar}`}
                alt=''
                style={avatarImg}
              />
            ) : (
              <AccountCircleIcon style={{ fontSize: '2rem', color: 'rgba(255,255,255,0.4)' }} />
            )}
          </div>
          <div style={headerInfo}>
            <span style={displayNameStyle}>{displayName}</span>
            <span style={balanceLabelStyle}>BALANCE</span>
            <span style={balanceLineStyle}>
              <span style={balanceMutedStyle}>Cash: </span>
              <span style={cashAmountStyle}>{currencyFormatter(cashBalance || 0)}</span>
            </span>
            <span style={balanceLineStyle}>
              <span style={balanceMutedStyle}>Promo: </span>
              <span style={promoAmountStyle}>{currencyFormatter(promoBalance || 0)}</span>
            </span>
          </div>
        </div>
        <button onClick={onClose} style={closeBtn} aria-label='Close' {...iconBtnHover}>
          <CloseIcon style={{ fontSize: '1.2rem' }} />
        </button>
      </div>

      <div className='account-panel-scroll' style={contentStyle}>
        {activePromo && (
          <AccountPromoBanner
            promo={activePromo as any}
            onGetMoreInfo={() => setSelectedPromo(activePromo as Promotion)}
          />
        )}

        <div style={menuListStyle}>
          {menuItems.map((item) => {
            // Disabled during maintenance (Cashier / Account Profile): dimmed,
            // no hover highlight, no navigation.
            const isDisabled = !!item.disabled;
            const isHovered = !isDisabled && hoveredKey === item.key;
            return (
              <button
                key={item.key}
                disabled={isDisabled}
                aria-disabled={isDisabled}
                style={{
                  ...menuRowStyle,
                  backgroundColor: isHovered ? MENU_ROW_HOVER_BG : 'transparent',
                  ...(isDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : {})
                }}
                onClick={isDisabled ? undefined : item.onClick}
                onMouseEnter={isDisabled ? undefined : () => setHoveredKey(item.key)}
                onMouseLeave={() => setHoveredKey(null)}
                onTouchStart={isDisabled ? undefined : () => setHoveredKey(item.key)}
                onTouchEnd={() => setHoveredKey(null)}
                onTouchCancel={() => setHoveredKey(null)}>
                <span
                  style={{
                    ...menuRowIconWrap,
                    borderColor: isHovered ? 'rgba(232,130,31,0.5)' : 'rgba(232,130,31,0.32)'
                  }}>
                  {cloneElement(item.icon as ReactElement, {
                    style: {
                      ...menuIconStyle,
                      color: isHovered ? MENU_ACCENT : menuIconStyle.color
                    }
                  })}
                </span>
                <span style={menuRowMiddleStyle}>
                  <span style={menuRowLabelStyle}>{item.label}</span>
                  {item.badge ? (
                    <span style={menuRowBadgeStyle} aria-label={`${item.badge} unread`}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  ) : null}
                </span>
                <ChevronRightIcon
                  style={{
                    ...menuChevronStyle,
                    color: isHovered ? MENU_ACCENT : menuChevronStyle.color
                  }}
                />
              </button>
            );
          })}

          <button
            style={{
              ...menuRowStyle,
              ...menuRowDangerStyle,
              backgroundColor: hoveredKey === 'logout' ? MENU_ROW_DANGER_HOVER_BG : 'transparent'
            }}
            onClick={handleLogout}
            onMouseEnter={() => setHoveredKey('logout')}
            onMouseLeave={() => setHoveredKey(null)}
            onTouchStart={() => setHoveredKey('logout')}
            onTouchEnd={() => setHoveredKey(null)}
            onTouchCancel={() => setHoveredKey(null)}>
            <span
              style={{
                ...menuRowIconWrap,
                borderColor:
                  hoveredKey === 'logout' ? 'rgba(192,57,43,0.5)' : 'rgba(255,255,255,0.12)'
              }}>
              <LogoutIcon style={{ ...menuIconStyle, color: '#c0392b' }} />
            </span>
            <span style={{ ...menuRowLabelStyle, color: '#c0392b' }}>Log Out</span>
            <ChevronRightIcon style={{ ...menuChevronStyle, color: 'rgba(192,57,43,0.5)' }} />
          </button>

          {/* Last thing in the panel, below Log Out. It is a readout for
              support, not a header field — up beside the balance it competed
              with the numbers a player actually opens this panel to see. */}
          <VersionLabel style={versionFooterStyle} />
        </div>
      </div>
    </>
  );

  // Returns whether the account-level flag was actually confirmed updated.
  // Callers must not treat the toggle as settled on `false` — the device
  // credential and the account flag disagreeing is what leaves a player with
  // a biometric login the next attempt then fails or a setting that silently
  // didn't take.
  //
  // `intent: 'decline'` marks "Not now" on the enrollment prompt: recorded
  // without revoking sessions. Without it, `enabled=false` is a real disable,
  // which revokes every session (EN-10901).
  const updateSecureLoginFlag = async (
    enabled: boolean,
    intent?: 'decline'
  ): Promise<boolean> => {
    const { access_token } = getAuthToken();
    let userId = playerGUID;
    if (!userId && access_token) {
      try {
        const payload = decodeJwtPayload(access_token);
        userId = payload.sub || '';
      } catch (_e) {
        /* ignore */
      }
    }
    if (!userId || !access_token) return false;
    let accountEnabled = enabled;
    try {
      const response = await fetch(`${BACKOFFICE_API_BASE}/descope/updateSecureLogin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
          Authorization: `Bearer ${access_token}`
        },
        body: new URLSearchParams({
          userId,
          enableSecureLogin: enabled ? '1' : '0',
          ...(intent ? { intent } : {})
        }).toString()
      });
      const body = await response.json().catch(() => null);
      if (!response.ok || body?.success !== true) return false;
      // A decline leaves an account enabled elsewhere at `true`; mirror what
      // the account actually holds, not what was asked for.
      if (typeof body.enableSecureLogin === 'boolean') accountEnabled = body.enableSecureLogin;
    } catch (err) {
      console.error('[Biometrics] Failed to sync enableSecureLogin:', err);
      return false;
    }
    dispatch(patchPlayerProfile({ enableSecureLogin: accountEnabled }));
    try {
      localStorage.setItem('hj_biometric_enabled', String(accountEnabled));
    } catch (_e) {
      /* ignore */
    }
    return true;
  };

  const handleBiometricToggle = async (enable: boolean) => {
    if (isNativeApp) {
      if (!enable) {
        // Deliberately cleared BEFORE the account-level sync, not after:
        // `enableSecureLogin` is a reconciliation/UI preference (drives the
        // Settings row's copy and whether other devices see biometrics as
        // "on" for this account) — it never gates login. Password/social
        // sign-in is always available regardless of its value; see
        // LoginApp.js/AccountMenuPanel/ProfilePanel, none of which check it
        // before allowing a normal login. So there is no "account locked
        // out" failure mode here. The reverse ordering (sync first, clear
        // after) would be the worse trade: if the account-level sync
        // succeeded but the LOCAL clear then failed, this device would still
        // hold a live, biometric-unlockable credential the player just
        // explicitly asked to turn off — exactly the "device honors
        // biometrics the account says are off" state the enroll path already
        // guards against in the opposite direction (see updateSecureLoginFlag
        // callers below: "don't leave a device credential the account-level
        // flag disagrees with"). Clearing local state first means a failed
        // sync degrades to "briefly out of sync until retried", not a live
        // credential outliving the player's explicit disable.
        const deleted = await disableNativeBiometric();
        setPasskeyEnrolled(false);
        const synced = await updateSecureLoginFlag(false);
        setBiometricMsg(
          !deleted.ok
            ? 'Biometric login is off on this device, but a stored credential could not be fully cleared. If this persists, reinstalling the app will remove it.'
            : synced
            ? 'Biometric login disabled.'
            : 'Biometric login was disabled on this device, but syncing that change to your account failed. Please try again.'
        );
        return;
      }
      const { refresh_token: refreshToken } = getAuthToken();
      if (!playerEmail || !refreshToken) {
        setBiometricMsg('Could not enable biometric login. Please try again.');
        return;
      }
      setIsEnrollingBiometric(true);
      try {
        const result = await enrollNativeBiometric(playerEmail, refreshToken);
        if (result.ok) {
          const synced = await updateSecureLoginFlag(true);
          if (!synced) {
            // Backend rejected the opt-in (kill switch, or a genuine failure)
            // — don't leave a device credential the account-level flag
            // disagrees with.
            await disableNativeBiometric();
            setPasskeyEnrolled(false);
            setBiometricMsg('Biometric login is temporarily unavailable. Please try again later.');
          } else {
            setPasskeyEnrolled(true);
            setBiometricMsg('Biometric login enabled on this device!');
          }
        } else {
          setPasskeyEnrolled(false);
          setBiometricMsg(`Setup failed: ${result.error || 'cancelled or unavailable'}`);
        }
      } finally {
        setIsEnrollingBiometric(false);
      }
      return;
    }

    const parts = window.location.hostname.split('.');
    const domain = parts.length >= 2 ? parts.slice(-2).join('.') : '';
    const domainAttr = domain ? `domain=.${domain}; ` : '';
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';

    if (!enable) {
      await clearBiometricCredentials(playerEmail);
      setPasskeyEnrolled(false);
      const synced = await updateSecureLoginFlag(false);
      setBiometricMsg(
        synced
          ? 'Biometric login disabled for this device.'
          : 'Biometric login was disabled on this device, but syncing that change to your account failed. Please try again.'
      );
      return;
    }

    const { refresh_token: refreshToken } = getAuthToken();
    if (!playerEmail || !refreshToken) {
      setBiometricMsg('Could not start enrollment. Please try again.');
      return;
    }

    setIsEnrollingBiometric(true);
    setBiometricMsg('');
    try {
      try {
        localStorage.setItem(`hj_biometric_rt_${playerEmail}`, refreshToken);
      } catch {
        /* ignore */
      }

      const hostname = window.location.hostname;
      const hostParts = hostname.split('.');
      const rpId =
        !hostname || hostname === 'localhost'
          ? 'localhost'
          : hostParts.length > 2
          ? hostParts.slice(-2).join('.')
          : hostname;

      let credId: string | null = null;
      try {
        const cred = await navigator.credentials.create({
          publicKey: {
            challenge: crypto.getRandomValues(new Uint8Array(32)).buffer,
            rp: { id: rpId, name: 'Hijack Poker' },
            user: {
              id: crypto.getRandomValues(new Uint8Array(16)).buffer,
              name: playerEmail,
              displayName: playerEmail
            },
            pubKeyCredParams: [
              { type: 'public-key', alg: -7 },
              { type: 'public-key', alg: -257 }
            ],
            authenticatorSelection: {
              authenticatorAttachment: 'platform',
              // 'required', not 'preferred': this credential is labeled and
              // trusted as "biometric login" everywhere downstream — a
              // ceremony that merely prefers verification can succeed with
              // none at all, which would enroll a device with no proof a
              // fingerprint/face/PIN check ever happened.
              userVerification: 'required'
            },
            timeout: 60000
          }
        } as CredentialCreationOptions);
        if (cred) {
          const rawId = (cred as PublicKeyCredential).rawId;
          credId = btoa(Array.from(new Uint8Array(rawId), (b) => String.fromCharCode(b)).join(''))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
        }
      } catch (err: any) {
        console.error('[Biometrics] credential create error:', err?.name, err?.message);
        if (err?.name === 'NotAllowedError') {
          // User cancelled or device denied — silently undo
          try {
            localStorage.removeItem(`hj_biometric_rt_${playerEmail}`);
          } catch {
            /* ignore */
          }
          setIsEnrollingBiometric(false);
          return;
        }
        if (err?.name === 'SecurityError') {
          setBiometricMsg('Biometric setup requires a secure (HTTPS) connection.');
          setIsEnrollingBiometric(false);
          return;
        }
        if (err?.name === 'NotSupportedError') {
          setBiometricMsg('This device or browser does not support biometric login.');
          setIsEnrollingBiometric(false);
          return;
        }
        throw err;
      }

      if (!credId) {
        try {
          localStorage.removeItem(`hj_biometric_rt_${playerEmail}`);
        } catch {
          /* ignore */
        }
        setBiometricMsg('Biometric setup is not supported on this device.');
        setIsEnrollingBiometric(false);
        return;
      }

      try {
        localStorage.setItem(`hj_biometric_cred_${playerEmail}`, credId);
      } catch {
        /* ignore */
      }

      const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
      // Write RT and credId to parent-domain cookies so the auth app (different
      // subdomain, separate localStorage) can read them on the login screen.
      document.cookie = `hj_biometric_rt=${encodeURIComponent(
        refreshToken
      )}; path=/; ${domainAttr}expires=${expires}; SameSite=Lax${secure}`;
      document.cookie = `hj_biometric_cred=${encodeURIComponent(
        credId
      )}; path=/; ${domainAttr}expires=${expires}; SameSite=Lax${secure}`;
      document.cookie = `hj_passkey=enrolled; path=/; ${domainAttr}expires=${expires}; SameSite=Lax${secure}`;
      document.cookie = `hj_passkey_email=${encodeURIComponent(
        playerEmail
      )}; path=/; ${domainAttr}expires=${expires}; SameSite=Lax${secure}`;
      const synced = await updateSecureLoginFlag(true);
      if (!synced) {
        // Backend rejected the opt-in (kill switch, or a genuine failure) —
        // don't leave a local biometric shortcut the account-level flag
        // disagrees with.
        await clearBiometricCredentials(playerEmail);
        setPasskeyEnrolled(false);
        setBiometricMsg('Biometric login is temporarily unavailable. Please try again later.');
      } else {
        setPasskeyEnrolled(true);
        setBiometricMsg('Biometric login enabled on this device!');
      }
    } catch (err: any) {
      console.error('[Biometrics] setup failed:', err?.name, err?.message);
      try {
        localStorage.removeItem(`hj_biometric_rt_${playerEmail}`);
      } catch {
        /* ignore */
      }
      setBiometricMsg(`Biometric setup failed: ${err?.name || 'unknown error'}. Please try again.`);
    } finally {
      setIsEnrollingBiometric(false);
    }
  };

  const settingsView = (
    <>
      <div style={subHeaderStyle}>
        <button
          onClick={() => setSubView(null)}
          style={subBackBtn}
          aria-label='Back'
          {...iconBtnHover}>
          <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
        </button>
        <span style={subHeaderTitle}>Settings</span>
        <span style={{ width: 28 }} aria-hidden />
      </div>
      <div className='account-panel-scroll' style={contentStyle}>
        {/* Lobby Settings group — controls that shape the lobby screen (not the
            table). Lobby Tiles Size moved here from the Table Filters drawer so
            it lives with the other player preferences. */}
        <span style={settingsGroupHeader}>Lobby Settings</span>
        <div style={{ ...settingsRowStyle, alignItems: 'flex-start', cursor: 'default' }}>
          <span style={settingsRowIconWrap}>
            <GridViewOutlinedIcon style={settingsRowIconStyle} />
          </span>
          <div style={settingsRowText}>
            <span style={settingsRowLabel}>Lobby Tiles Size</span>
            <span style={settingsRowDesc}>
              Compact fits more games per screen; Large shows bigger tiles.
            </span>
            <div style={cardSizeSegRow}>
              {(['compact', 'large'] as const).map((size) => {
                const on = cardSize === size;
                return (
                  <button
                    key={size}
                    type='button'
                    onClick={() => setCardSize(size)}
                    aria-pressed={on}
                    style={{ ...cardSizeSegBtn, ...(on ? cardSizeSegBtnOn : {}) }}>
                    {size === 'compact' ? 'Compact' : 'Large'}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* All Tables — settings that shape the nav across the lobby, the grid
            and every table rather than one table's felt. The in-table menu has
            the same group, so it is reachable with a single table open. */}
        <span style={settingsGroupHeader}>All Tables</span>
        <SettingsToggleRow
          icon={<ViewAgendaOutlinedIcon style={settingsRowIconStyle} />}
          {...stackTablesToggle}
        />

        {/* Table Settings group — mirrors the in-table Table Settings panel
            (TableMenuPanel) so the lobby and table share one look & feel. */}
        <span style={settingsGroupHeader}>Table Settings</span>

        {/* Table View Size — cards + money/pot/chip text scale (Small → Largest). */}
        <div style={{ ...settingsRowStyle, alignItems: 'flex-start', cursor: 'default' }}>
          <span style={settingsRowIconWrap}>
            <FormatSizeIcon style={settingsRowIconStyle} />
          </span>
          <div style={settingsRowText}>
            <span style={settingsRowLabel}>Table View Size</span>
            <span style={settingsRowDesc}>
              Cards, seat names and money/pot/chip text — Small to Largest.
            </span>
            {/* Shared with TableMenuPanel — the player can reach this control
                from either menu, so it must behave identically in both. */}
            <TableViewSizeControl testIdPrefix='account-table-view-size' />
            <TableViewSizePreview scale={tableSettings.tableViewScale} />
          </div>
        </div>

        {/* Table theme — the "Table Settings" theme picker ported from the old
            ProfilePanel so it's available in the consistent account menu. */}
        <button
          type='button'
          onClick={() => {
            setDraftTheme(tableSettings.theme);
            setSubView('tableTheme');
          }}
          style={settingsRowStyle}
          {...hoverStyle(
            { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(232,130,31,0.4)' },
            { background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(232,130,31,0.18)' }
          )}>
          <span style={settingsRowIconWrap}>
            <PaletteIcon style={settingsRowIconStyle} />
          </span>
          <div style={settingsRowText}>
            <span style={settingsRowLabel}>Change table theme</span>
            <span style={settingsRowDesc}>{TABLE_THEMES[tableSettings.theme].name}</span>
          </div>
          <ChevronRightIcon style={{ ...menuChevronStyle, color: 'rgba(255,255,255,0.4)' }} />
        </button>
        <SettingsToggleRow
          icon={<ChatBubbleOutlineIcon style={settingsRowIconStyle} />}
          label='Table chat enabled'
          description='Show chat messages at the poker table'
          checked={tableSettings.chatEnabled}
          onChange={tableSettings.setChatEnabled}
        />
        <SettingsToggleRow
          icon={<NotificationsOffOutlinedIcon style={settingsRowIconStyle} />}
          label='Table sounds disabled'
          description='Silence card deals, chips, and timer alerts'
          checked={isMuted}
          onChange={setIsMuted}
        />
        {/* Hidden while ALL table sounds are off — it would have no effect, and
            Unity hides it the same way (MenuForTablesView.cs:120/242/493). The
            saved value is untouched, so re-enabling sounds brings the row back
            exactly as the player left it. */}
        {!isMuted && (
          <SettingsToggleRow
            icon={<PersonOffOutlinedIcon style={settingsRowIconStyle} />}
            label="Mute Other Players' Sounds"
            description='Silence sound effects from other players at the table.'
            checked={tableSettings.muteOtherPlayers}
            onChange={tableSettings.setMuteOtherPlayers}
          />
        )}
        {/* EN-11003: account-wide setting behind release.reduce-animations. */}
        {animations.available && (
          <>
            <SettingsToggleRow
              icon={<AnimationIcon style={settingsRowIconStyle} />}
              label='Reduce animations'
              description='Skip confetti and shorten win celebrations'
              checked={animations.ready && animations.reduceAnimations}
              disabled={!animations.ready || animations.busy}
              onChange={animations.setReduceAnimations}
            />
            {animations.busy && (
              <span role='status' style={settingsRowDesc}>
                Updating animation settings…
              </span>
            )}
            {animations.error && (
              <div role='alert' style={settingsRowDesc}>
                {animations.error}{' '}
                <button type='button' onClick={animations.retry}>
                  Try again
                </button>
              </div>
            )}
          </>
        )}

        {(performanceHudAvailable || systemLogAvailable) && (
          <>
            <span style={settingsGroupHeader}>Advanced</span>
            {performanceHudAvailable && (
              <SettingsToggleRow
                icon={<BarChartIcon style={settingsRowIconStyle} />}
                label='Performance HUD'
                description='Show live client performance diagnostics'
                checked={performanceHudOn}
                onChange={(next) => {
                  setPlayerPerfHudEnabled(playerGUID, next);
                  setPerformanceHudOn(next);
                  trackPendoEvent({
                    playerGUID,
                    eventName: 'performance_hud_preference_changed',
                    properties: {
                      flag_key: FEATURE_FLAGS.performanceHud,
                      variation: 'on',
                      selected_path: FEATURE_FLAG_PATHS.performanceHud(true).path,
                      surface: 'settings',
                      enabled: next
                    }
                  });
                }}
              />
            )}
            {systemLogAvailable && (
              <SettingsToggleRow
                icon={<TerminalIcon style={settingsRowIconStyle} />}
                label='System Log'
                description='Show the in-app debug console overlay'
                checked={debugConsoleOn}
                onChange={(next) => {
                  setDebugConsoleEnabled(playerGUID, next);
                  setDebugConsoleOn(next);
                  trackPendoEvent({
                    playerGUID,
                    eventName: 'system_log_preference_changed',
                    properties: {
                      flag_key: FEATURE_FLAGS.systemLog,
                      variation: 'on',
                      selected_path: FEATURE_FLAG_PATHS.systemLog(true).path,
                      surface: 'settings',
                      enabled: next
                    }
                  });
                }}
              />
            )}
          </>
        )}
        {/* Biometrics enrollment prompt for new users (enableSecureLogin never set) */}
        {profileLoaded &&
          enableSecureLogin === null &&
          biometricAvailableHere &&
          canEnrollBiometric &&
          !passkeyEnrolled && (
            <div style={biometricPromptStyle}>
              <FingerprintIcon style={{ fontSize: '1.4rem', color: '#E98C26', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={biometricPromptTitle}>Enable biometric login?</div>
                <div style={biometricPromptDesc}>
                  Sign in faster with Face ID or fingerprint — nothing to type.
                </div>
                <div style={biometricPromptActions}>
                  <button
                    style={biometricPromptEnableBtn}
                    disabled={isEnrollingBiometric}
                    onClick={() => handleBiometricToggle(true)}>
                    {isEnrollingBiometric ? 'Setting up…' : 'Enable'}
                  </button>
                  <button
                    style={biometricPromptDismissBtn}
                    onClick={() => updateSecureLoginFlag(false, 'decline')}>
                    Not now
                  </button>
                </div>
              </div>
            </div>
          )}
        {/* AC11a — ALWAYS rendered for signed-in players. This used to be gated on
            `biometricAvailableHere &&`, so on a device with no face/finger set
            up the row was not off, it was ABSENT: combined with the enrolment
            offer clearing itself in silence, the feature was undiscoverable
            from anywhere in the product for everyone who unlocks their phone
            with a passcode (found on-device 2026-08-19). It now stays, states
            why it can't be used, and — where the player can act — opens the
            explainer instead of toggling. */}
        {canEnrollBiometric && (
          <SettingsToggleRow
            // The sensor the DEVICE actually has, in the same cream every other
            // settings icon wears. The wrap's green ON-tint is skipped on
            // purpose: state already lives in the toggle, and a lone green icon
            // in a cream column read as a warning, not a state
            // (owner-caught on-device 2026-08-20).
            icon={
              <span style={{ color: settingsRowIconStyle.color, display: 'flex' }}>
                <SensorGlyph kind={biometricLabelKind} size={20} />
              </span>
            }
            label='Biometric login'
            description={
              isEnrollingBiometric
                ? 'Setting up biometrics…'
                : biometricBlocked === 'not-enrolled'
                ? `${biometryTitle(
                    biometricLabelKind
                  )} isn’t set up on this device — tap to see how`
                : biometricBlocked === 'locked-out'
                ? 'Locked after too many attempts — unlock this device to clear it'
                : biometricBlocked === 'unsupported'
                ? isNativeApp
                  ? 'This device has no fingerprint or face sensor'
                  : 'This browser can’t use biometric sign-in'
                : passkeyEnrolled
                ? 'Enabled — tap to disable on this device'
                : biometricMsg || `Use ${biometryTitle(biometricLabelKind)} to sign in`
            }
            checked={passkeyEnrolled && !biometricBlocked}
            disabled={isEnrollingBiometric || biometricBlocked === 'unsupported'}
            onChange={biometricBlocked ? () => setShowBiometricHelp(true) : handleBiometricToggle}
          />
        )}
        {/* Pull, not push (Jason 2026-08-19): the explainer is reached from
            here, never shoved in front of a player at login. iOS already keeps
            its own badge nagging about finishing Face ID setup.

            No `native` override on the host: its own rule — drawer on mobile,
            centred dialog on desktop — is the whole reason it exists, and
            hard-coding a drawer put a bottom sheet on desktop web. */}
        {showBiometricHelp && biometricBlocked && (
          <BiometricSurfaceHost
            onDismiss={() => setShowBiometricHelp(false)}
            ariaLabel='Biometric sign-in unavailable'>
            <BiometricUnavailableCard
              skin='hijack'
              biometry={biometricLabelKind}
              reason={biometricBlocked}
              onAction={() => {
                // "Open iOS Settings" does what it says (app-settings: —
                // public scheme, one back-tap from Face ID & Passcode; see
                // openIOSSettings). The card closes so the player returns to a
                // settled panel, and the open-effect re-checks the blocked
                // reason when they come back.
                if (biometricBlocked === 'not-enrolled') openIOSSettings();
                setShowBiometricHelp(false);
              }}
              onDismiss={() => setShowBiometricHelp(false)}
            />
          </BiometricSurfaceHost>
        )}
      </div>
    </>
  );

  const tableThemeView = (
    <>
      <div style={subHeaderStyle}>
        <button
          onClick={() => setSubView('settings')}
          style={subBackBtn}
          aria-label='Back'
          {...iconBtnHover}>
          <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
        </button>
        <span style={subHeaderTitle}>Table Theme</span>
        <span style={{ width: 28 }} aria-hidden />
      </div>
      <div className='account-panel-scroll' style={{ ...contentStyle, paddingBottom: 90 }}>
        {/* From the lobby, the theme is the player's global default. */}
        <p style={themeScopeNoteStyle}>Applies to all my tables.</p>
        <div style={themeGridStyle}>
          {TABLE_THEME_ORDER.map((id) => {
            const t = TABLE_THEMES[id];
            const isSelected = draftTheme === id;
            return (
              <button
                key={id}
                onClick={() => setDraftTheme(id)}
                style={{
                  ...themeCardStyle,
                  borderColor: isSelected ? '#E98C26' : 'rgba(255,255,255,0.08)',
                  boxShadow: isSelected ? '0 0 0 1px #E98C26' : 'none'
                }}>
                <div style={{ ...themePreviewStyle, backgroundColor: t.swatch.outer }}>
                  <div
                    style={{
                      ...themePreviewOvalStyle,
                      backgroundColor: t.swatch.felt,
                      boxShadow: `0 0 0 1px ${t.swatch.ring}, inset 0 0 12px rgba(0,0,0,0.4)`
                    }}
                  />
                </div>
                <span style={themeNameStyle}>{t.name}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div style={themeSaveBarStyle}>
        <button
          onClick={() => {
            tableSettings.setTheme(draftTheme);
            setSubView('settings');
          }}
          style={themeSaveBtn}
          disabled={draftTheme === tableSettings.theme}>
          Save changes
        </button>
      </div>
    </>
  );

  const notificationsView = (
    <>
      <div style={subHeaderStyle}>
        <button
          onClick={() => (notifFromLobby ? onClose() : setSubView(null))}
          style={subBackBtn}
          aria-label='Back'
          {...iconBtnHover}>
          <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
        </button>
        <span style={subHeaderTitle}>Notifications</span>
        <span style={{ width: 28 }} aria-hidden />
      </div>
      {/* Real notifications from the shared store — same list the bell renders
          (see EN-9838 QA: this view was a static empty placeholder). */}
      <NotificationList
        className='account-panel-scroll'
        notifications={sortedNotifications}
        onNotificationClick={markNotificationRead}
      />
    </>
  );

  return createPortal(
    <>
      <style>{accountPanelScrollbarCss}</style>
      {/* Rotate WITH the portrait-locked lobby on mobile-landscape — this panel
          portals to <body>, outside the lobby's wrapper, so the rotation lives
          inside the portal. */}
      <ForcePortrait>
        <div ref={backdropRef} style={backdropStyle} onClick={onClose} />
        <div ref={panelRef} style={isPortrait ? panelMobile : panelDesktop}>
          {subView === null && menuView}
          {subView === 'notifications' && notificationsView}
          {subView === 'settings' && settingsView}
          {subView === 'tableTheme' && tableThemeView}
        </div>
      </ForcePortrait>
      {/* Account Profile renders as its own portal/backdrop on top of this menu */}
      <ProfilePanel open={subView === 'profile'} onClose={() => setSubView(null)} embedded />
      {selectedPromo && (
        <PromoDetailsModal promo={selectedPromo} onClose={() => setSelectedPromo(null)} />
      )}
    </>,
    document.body
  );
};

export default AccountMenuPanel;

const SettingsToggleRow = ({
  icon,
  label,
  description,
  checked,
  disabled,
  onChange
}: {
  icon?: ReactElement;
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) => (
  <button
    type='button'
    onClick={() => !disabled && onChange(!checked)}
    style={{ ...settingsRowStyle, opacity: disabled ? 0.6 : 1 }}
    aria-pressed={checked}
    disabled={disabled}
    {...hoverStyle(
      { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(232,130,31,0.4)' },
      { background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(232,130,31,0.18)' }
    )}>
    {icon && (
      <span
        style={{ ...settingsRowIconWrap, color: checked ? '#4CAF50' : 'rgba(255,255,255,0.3)' }}>
        {icon}
      </span>
    )}
    <div style={settingsRowText}>
      <span style={settingsRowLabel}>{label}</span>
      {description && <span style={settingsRowDesc}>{description}</span>}
    </div>
    <span
      style={{
        ...toggleTrackStyle,
        background: checked ? 'rgba(232,130,31,0.7)' : 'rgba(255,255,255,0.12)',
        borderColor: checked ? 'rgba(232,130,31,0.9)' : 'rgba(255,255,255,0.2)'
      }}>
      <span
        style={{
          ...toggleKnobStyle,
          transform: checked ? 'translateX(18px)' : 'translateX(0)'
        }}
      />
    </span>
  </button>
);

/* ─────────── styles ─────────── */

const accountPanelScrollbarCss = `
  .account-panel-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(233, 140, 38, 0.3) transparent;
  }

  .account-panel-scroll::-webkit-scrollbar {
    width: 6px;
  }

  .account-panel-scroll::-webkit-scrollbar-track {
    background: transparent;
  }

  .account-panel-scroll::-webkit-scrollbar-thumb {
    background: rgba(233, 140, 38, 0.3);
    border-radius: 999px;
  }
`;

const backdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  zIndex: 900,
  opacity: 0
};

const panelBase: CSSProperties = {
  position: 'fixed',
  backgroundColor: 'rgba(12,18,28,0.98)',
  backdropFilter: 'blur(20px)',
  zIndex: 950,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  // Honor device safe areas so the header doesn't sit under the notch/status
  // bar and the scrollable content clears the home indicator in portrait.
  ...safeAreaPanelInsets
};

const panelMobile: CSSProperties = {
  ...panelBase,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  height: '100vh',
  boxShadow: '0 -8px 40px rgba(0,0,0,0.5)'
};

const panelDesktop: CSSProperties = {
  ...panelBase,
  top: 0,
  right: 0,
  bottom: 0,
  width: '380px',
  borderTopLeftRadius: '16px',
  borderBottomLeftRadius: '16px',
  boxShadow: '-8px 0 40px rgba(0,0,0,0.5)',
  borderLeft: '1px solid rgba(255,255,255,0.06)'
};

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '20px 20px 16px',
  flexShrink: 0,
  // Fine, faint dashed divider — matches the promo banner's thin dashed border.
  borderBottom: '1px dashed rgba(232,130,31,0.18)'
};

const avatarSection: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  flex: 1
};

const avatarWrap: CSSProperties = {
  position: 'relative',
  width: 52,
  height: 52,
  borderRadius: '50%',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '2px solid rgba(233,140,38,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flexShrink: 0
};

const avatarImg: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '50%'
};

const headerInfo: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0
};

const displayNameStyle: CSSProperties = {
  fontFamily: "'Rye', serif",
  fontSize: '18px',
  color: '#ffb45c', // var(--orange-300)
  letterSpacing: '0.02em',
  lineHeight: 1,
  textShadow: '0 1px 0 rgba(0, 0, 0, 0.55)',
  wordBreak: 'break-word',
  marginBottom: 2
};

const balanceLabelStyle: CSSProperties = {
  fontFamily: "'Special Elite', monospace",
  fontSize: '9.5px',
  color: '#d9c79e', // var(--bone-2)
  opacity: 0.7,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  marginTop: 2
};

const balanceLineStyle: CSSProperties = {
  fontSize: '0.78rem',
  fontWeight: 600
};

const balanceMutedStyle: CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontWeight: 500,
  fontSize: '12px',
  color: '#d9c79e' // var(--bone-2)
};

const cashAmountStyle: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '13.5px',
  color: '#f1e3c7', // var(--bone)
  letterSpacing: '0.01em'
};

const promoAmountStyle: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '13.5px',
  color: '#ffb45c', // var(--orange-300)
  letterSpacing: '0.01em',
  textShadow: '0 0 6px rgba(232, 130, 31, 0.25)'
};

const versionFooterStyle: CSSProperties = {
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.7rem',
  letterSpacing: '0.02em',
  color: 'rgba(241, 227, 199, 0.45)',
  justifyContent: 'center',
  marginTop: 10
};

const closeBtn: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: 'none',
  borderRadius: '999px',
  width: 28,
  height: 28,
  color: 'rgba(255,255,255,0.6)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'background-color 0.15s ease, color 0.15s ease'
};

const contentStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  padding: '16px',
  overflowY: 'auto'
};

// Hover tints for the menu rows — applied via mouse handlers because these
// rows use inline CSSProperties (no :hover). The Claude design highlights the
// row under the pointer (see EN-9838 QA).
const MENU_ROW_HOVER_BG = 'rgba(255,255,255,0.06)';
const MENU_ROW_DANGER_HOVER_BG = 'rgba(192,57,43,0.12)';
// Saloon orange — icons/chevrons adopt it on row hover.
const MENU_ACCENT = '#E98C26';

const menuListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
};

const menuRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '38px 1fr 14px',
  columnGap: '12px',
  alignItems: 'center',
  width: '100%',
  padding: '13px 4px',
  background: 'transparent',
  border: 0,
  borderBottom: '1px dotted rgba(255, 255, 255, 0.06)',
  color: '#f1e3c7', // var(--bone)
  font: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 0.15s ease, color 0.15s ease'
};

const menuRowDangerStyle: CSSProperties = {
  marginTop: 6
};

const menuRowIconWrap: CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(232, 130, 31, 0.32)',
  color: '#f1e3c7', // var(--bone)
  display: 'grid',
  placeItems: 'center',
  justifySelf: 'center',
  flexShrink: 0,
  transition: 'border-color 0.15s ease'
};

// Middle grid cell of a menu row — holds the label (flex:1) plus the optional
// unread badge, so the row's grid stays a clean 3 columns (icon / label / chevron).
const menuRowMiddleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0
};

const menuIconStyle: CSSProperties = {
  fontSize: '1.05rem',
  color: '#f1e3c7', // var(--bone) — Log Out overrides this to red
  transition: 'color 0.15s ease'
};

const menuRowLabelStyle: CSSProperties = {
  flex: 1,
  // Match the design's menu rows: Oswald, bold, in the warm "bone" tone (not pure
  // white — profile values are white, but the menu labels are bone). Log Out
  // overrides only the color (#c0392b).
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#f1e3c7'
};

const menuChevronStyle: CSSProperties = {
  fontSize: '1.1rem',
  color: 'rgba(255,255,255,0.3)',
  transition: 'color 0.15s ease'
};

// Red unread-count pill on the Notifications row — surfaces the same
// unreadCount the bell tracks, so the count is visible without opening the
// list (see EN-9838 follow-up).
const menuRowBadgeStyle: CSSProperties = {
  minWidth: 18,
  height: 18,
  padding: '0 5px',
  borderRadius: 999,
  background: '#C42021',
  color: '#fff',
  fontSize: '0.62rem',
  fontWeight: 700,
  lineHeight: '18px',
  textAlign: 'center',
  flexShrink: 0
};

/* sub-view header — chunky orange dashed underline matching the main panel
   and CashierPanel headers (Account Profile, Cashier, Settings all share this look). */
const subHeaderStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '28px 1fr 28px',
  alignItems: 'center',
  padding: '20px 20px 14px',
  backgroundImage:
    'repeating-linear-gradient(to right, rgba(232,130,31,0.55) 0 9px, transparent 9px 15px)',
  backgroundSize: '100% 2px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'bottom left',
  flexShrink: 0,
  textAlign: 'center' as const
};

const subBackBtn: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: 'none',
  borderRadius: '999px',
  width: 28,
  height: 28,
  color: 'rgba(255,255,255,0.6)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.15s ease, color 0.15s ease'
};

// .apn-title
const subHeaderTitle: CSSProperties = {
  flex: 1,
  textAlign: 'center',
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '18px',
  color: '#f1e3c7', // var(--bone)
  letterSpacing: '0.04em'
};

/* Settings sub-view rows */
const settingsRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  width: '100%',
  padding: '14px 12px',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(232,130,31,0.18)',
  borderRadius: 10,
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
  transition: 'background-color 0.15s ease, border-color 0.15s ease'
};
const settingsRowText: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  flex: 1,
  minWidth: 0
};
// Oswald title-case to match the in-table Table Settings panel (TableMenuPanel).
const settingsRowLabel: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.95rem',
  fontWeight: 700,
  letterSpacing: '0.01em',
  color: '#f1e3c7'
};
const settingsRowDesc: CSSProperties = {
  fontSize: '0.7rem',
  color: 'rgba(255,255,255,0.5)',
  lineHeight: 1.35
};
// Small caps group heading separating the Table Settings block from Advanced.
const settingsGroupHeader: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '0.7rem',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'rgba(232,130,31,0.85)',
  padding: '2px 2px'
};
// Bare leading icon slot for the settings rows (mirrors TableMenuPanel).
const settingsRowIconWrap: CSSProperties = {
  width: 28,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0
};
const settingsRowIconStyle: CSSProperties = {
  fontSize: '1.3rem',
  color: 'rgba(241, 227, 199, 0.9)'
};
// Segmented Compact/Large control for Lobby Tiles Size — orange-active pill
// pair, mirroring the drawer's old checked chips.
const cardSizeSegRow: CSSProperties = {
  display: 'flex',
  gap: 8,
  marginTop: 10,
  width: '100%'
};
const cardSizeSegBtn: CSSProperties = {
  flex: 1,
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid rgba(232,130,31,0.3)',
  background: 'rgba(255,255,255,0.03)',
  color: 'rgba(241,227,199,0.75)',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '0.85rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease'
};
const cardSizeSegBtnOn: CSSProperties = {
  background: 'rgba(232,130,31,0.7)',
  borderColor: 'rgba(232,130,31,0.9)',
  color: '#fff'
};
const toggleTrackStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
  width: 38,
  height: 22,
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(255,255,255,0.12)',
  flexShrink: 0,
  transition: 'background-color 0.18s ease, border-color 0.18s ease'
};
const toggleKnobStyle: CSSProperties = {
  position: 'absolute',
  top: 2,
  left: 2,
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#fff',
  boxShadow: '0 1px 2px rgba(0,0,0,0.35)',
  transition: 'transform 0.18s ease'
};

const biometricPromptStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '14px 12px',
  background: 'rgba(233,140,38,0.08)',
  border: '1px solid rgba(233,140,38,0.35)',
  borderRadius: 10
};
const biometricPromptTitle: CSSProperties = {
  fontFamily: "'Special Elite', monospace",
  fontSize: '0.78rem',
  letterSpacing: '0.1em',
  color: '#fff',
  textTransform: 'uppercase',
  marginBottom: 4
};
const biometricPromptDesc: CSSProperties = {
  fontSize: '0.7rem',
  color: 'rgba(255,255,255,0.55)',
  lineHeight: 1.4,
  marginBottom: 10
};
const biometricPromptActions: CSSProperties = {
  display: 'flex',
  gap: 8
};
const biometricPromptEnableBtn: CSSProperties = {
  padding: '6px 14px',
  background: 'linear-gradient(135deg, #d4a853 0%, #b8912e 100%)',
  border: 'none',
  borderRadius: 6,
  color: '#1a1408',
  fontSize: '0.72rem',
  fontWeight: 700,
  fontFamily: "'Oswald', sans-serif",
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  cursor: 'pointer'
};
const biometricPromptDismissBtn: CSSProperties = {
  padding: '6px 10px',
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.18)',
  borderRadius: 6,
  color: 'rgba(255,255,255,0.45)',
  fontSize: '0.72rem',
  fontFamily: "'Oswald', sans-serif",
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  cursor: 'pointer'
};
/* Table theme picker (ported from ProfilePanel Table Settings) */
const themeScopeNoteStyle: CSSProperties = {
  margin: '0 0 12px',
  color: 'rgba(255,255,255,0.55)',
  fontSize: '0.8rem'
};
const themeGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '14px'
};
const themeCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  padding: '14px 10px',
  backgroundColor: 'rgba(255,255,255,0.03)',
  border: '2px solid rgba(255,255,255,0.08)',
  borderRadius: '14px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
};
const themePreviewStyle: CSSProperties = {
  width: '100%',
  aspectRatio: '0.85 / 1',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};
const themePreviewOvalStyle: CSSProperties = {
  width: '70%',
  height: '70%',
  borderRadius: '999px'
};
const themeNameStyle: CSSProperties = {
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#fff'
};
const themeSaveBarStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  padding: '14px 20px',
  // Absolutely positioned, so it overlaps the panel's bottom safe-area padding;
  // add the inset to its own padding so the button clears the home indicator.
  paddingBottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
  borderTop: '1px solid rgba(255,255,255,0.06)',
  backgroundColor: 'rgba(12,18,28,0.98)',
  flexShrink: 0
};
const themeSaveBtn: CSSProperties = {
  width: '100%',
  padding: '12px',
  background: 'linear-gradient(135deg, #E98C26, #d47a1a)',
  border: 'none',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.95rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
